import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/** Counts predictive search components on the page, so option ids stay unique. */
let predictiveSearchCount = 0;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      // aria-expanded follows the dialog: the results show exactly while it is open
      dialog.addEventListener(DialogOpenEvent.eventName, () => this.#syncCombobox(), { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.#syncCombobox();

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#collapse();
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  /*
   * SE2 combobox (QA 2026-10-06, V-header-02). The field is an ARIA 1.2 combobox: DOM focus stays in it, the arrow
   * keys move the active option, and the field names that option through aria-activedescendant. The popup is the
   * [role="listbox"] the results markup carries (sections/predictive-search.liquid, snippets/predictive-search-empty-
   * state.liquid): query suggestions and product cards only. The browse pills, the kit tile and the collection, page
   * and article carousels sit outside it as ordinary links, reached with Tab. Each option is the result's own link,
   * marked here after every render because the product link comes from snippets/resource-card.liquid: role="option",
   * an id, aria-selected, and tabindex="-1" so Tab leaves the field for Clear and Close instead of walking every result
   * (it used to take 12 presses to reach Close). The highlight is drawn on the result's card (.keyboard-focus).
   */

  /** @type {number} */
  #uid = ++predictiveSearchCount;

  /** @returns {HTMLElement | null} the listbox in the current results, if they have one */
  get #listbox() {
    const listbox = this.refs.predictiveSearchResults.querySelector('[role="listbox"]');
    return listbox instanceof HTMLElement ? listbox : null;
  }

  /** @returns {HTMLElement[]} the options in the listbox, in order */
  get #options() {
    const listbox = this.#listbox;
    if (!listbox) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(listbox.querySelectorAll('[role="option"]')).filter((option) => option instanceof HTMLElement)
    );
  }

  /**
   * The card that carries an option's highlight.
   * @param {HTMLElement} option
   * @returns {HTMLElement}
   */
  #cardOf(option) {
    const card = option.closest('.predictive-search-results__card, .predictive-search-results__card--query');
    return card instanceof HTMLElement ? card : option;
  }

  /** Whether the results are on screen: while the dialog is open, or always when there is no dialog. */
  get #isShown() {
    if (!this.dialog) return true;
    return Boolean(this.closest('dialog')?.open);
  }

  /**
   * Marks every result link in the listbox as an option, and brings the field's aria-expanded, aria-controls and
   * aria-activedescendant in line with what is rendered. Runs after every render of the results.
   */
  #syncCombobox() {
    const { searchInput } = this.refs;
    const listbox = this.#listbox;
    /** @type {HTMLElement[]} */
    const options = [];

    if (listbox) {
      const cards = listbox.querySelectorAll('.predictive-search-results__card, .predictive-search-results__card--query');
      for (const card of cards) {
        // the card's one link (a product card's .resource-card__link, a suggestion's pill)
        const link = card.querySelector('a[href]');
        if (!(link instanceof HTMLElement)) continue;
        link.setAttribute('role', 'option');
        link.setAttribute('tabindex', '-1');
        if (link.getAttribute('aria-selected') !== 'true') link.setAttribute('aria-selected', 'false');
        options.push(link);
      }
      options.forEach((option, index) => {
        option.id = `predictive-search-${this.#uid}-option-${index + 1}`;
      });
      if (listbox.id) searchInput.setAttribute('aria-controls', listbox.id);
    }

    searchInput.setAttribute('aria-expanded', String(options.length > 0 && this.#isShown));

    const active = options.find((option) => option.getAttribute('aria-selected') === 'true');
    if (active) {
      searchInput.setAttribute('aria-activedescendant', active.id);
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }

  /** Closes the popup for assistive technology: nothing active, nothing expanded. */
  #collapse() {
    const { searchInput } = this.refs;
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.removeAttribute('aria-activedescendant');
  }

  get #currentIndex() {
    return this.#options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const options = this.#options;
    const { searchInput } = this.refs;

    if (!options.length) {
      searchInput.removeAttribute('aria-activedescendant');
      return;
    }

    /** @type {HTMLElement | null} */
    let activeOption = null;

    options.forEach((option, optionIndex) => {
      const selected = optionIndex === index;
      option.setAttribute('aria-selected', String(selected));
      this.#cardOf(option).classList.toggle('keyboard-focus', selected);
      if (selected) activeOption = option;
    });

    if (activeOption) {
      searchInput.setAttribute('aria-activedescendant', /** @type {HTMLElement} */ (activeOption).id);
      this.#cardOf(activeOption).scrollIntoView({
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'nearest',
      });
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }

    searchInput.setAttribute('aria-expanded', String(this.#isShown));
    searchInput.focus();
  }

  /**
   * In the field: the arrow keys move the active option (wrapping), Enter opens it, Escape clears the search.
   * Tab is the browser's own, so it leaves the field. Arrow keys and Enter pressed on any other control in the form
   * (Clear, Close, a carousel arrow, View all) are left to that control: Enter on Close used to run a search.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (document.activeElement !== this.refs.searchInput) return;

    const options = this.#options;
    const currentIndex = this.#currentIndex;
    const totalItems = options.length;

    switch (event.key) {
      case 'ArrowDown':
        if (!totalItems) return;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        if (!totalItems) return;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        const activeOption = options[currentIndex];
        if (activeOption) {
          event.preventDefault();
          activeOption.click();
        }
        // with no active option the form submits as usual: GET /search with the query
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * The "Recently viewed" heading sits above the listbox, apart from its group (see #resetSearch), so it collapses
   * and goes with the group. The Clear button pressed is inside that heading: when it held focus, focus returns to
   * the search field, so it stays in the dialog instead of falling to the page (V-header-02).
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;
    const titles = recentlyViewedTitle || [];

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...titles];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (titles.some((title) => title.contains(document.activeElement))) {
      this.refs.searchInput.focus();
    }

    const leaving = /** @type {HTMLElement[]} */ ([...titles, ...(recentlyViewedWrapper ? [recentlyViewedWrapper] : [])]);
    leaving.forEach((element) => element.classList.add('removing'));

    onAnimationEnd(leaving, () => {
      leaving.forEach((element) => element.remove());
      this.#syncCombobox();
    });
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);
        this.#syncCombobox();

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);

      // The "Recently viewed" heading holds the Clear button, and a listbox may hold only options and their groups
      // (V-header-02). It moves to just above the listbox. The recent group stays inside, still named by the
      // heading's title span, and is the listbox's first child, so nothing moves on screen.
      const listbox = parsedEmptySectionMarkup.querySelector('#predictive-search-listbox');
      const recentTitle = collectionElement.querySelector('[ref="recentlyViewedTitle[]"]');
      if (listbox && recentTitle) listbox.before(recentTitle);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#syncCombobox();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}

/*
 * SE2 (HDR-B-08, 2026-10-05): one search for the store. The menu drawer's field (snippets/header-drawer.liquid,
 * [data-se2-search-open]) stays a plain GET form to /search, so it works without JavaScript. With it, a tap or a
 * typed character in that field closes the drawer and opens this modal, the same showDialog the header's search icon
 * calls, then puts focus (and anything already typed) in the modal's field. The modal opens only after the drawer
 * has finished closing: the drawer's focus trap is lifted at the end of its animation and would pull focus back.
 * 2026-10-06: the drawer field keeps focus while the drawer slides out, so every key typed meanwhile lands in it; the
 * query is read when the modal opens, not at the first key, and handed over as a typed InputEvent so the modal
 * searches it (an input event without an inputType is ignored by search()). One handoff runs at a time.
 */
let se2HandoffPending = false;

const se2OpenSearchFromDrawer = (/** @type {HTMLInputElement} */ field) => {
  const modal = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (
    document.getElementById('search-modal')
  );
  if (!modal || typeof modal.showDialog !== 'function') return;
  if (se2HandoffPending) return;
  se2HandoffPending = true;

  let opened = false;
  const open = () => {
    if (opened) return;
    opened = true;
    const query = field.value;
    field.value = '';
    modal.showDialog?.();
    // showDialog opens on the next frame; focus once the dialog is open
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        se2HandoffPending = false;
        const input = modal.querySelector('input[type="search"]');
        if (!(input instanceof HTMLInputElement)) return;
        input.focus();
        if (query) {
          input.value = query;
          input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: query }));
        }
      })
    );
  };

  const drawer = /** @type {(HTMLElement & { close?: () => void }) | null} */ (field.closest('header-drawer'));
  const details = field.closest('details');
  if (drawer && typeof drawer.close === 'function' && details?.open) {
    details.addEventListener('toggle', open, { once: true });
    setTimeout(open, 800);
    drawer.close();
  } else {
    open();
  }
};

document.addEventListener('click', (event) => {
  const field = event.target instanceof Element ? event.target.closest('[data-se2-search-open]') : null;
  if (field instanceof HTMLInputElement) se2OpenSearchFromDrawer(field);
});

document.addEventListener('input', (event) => {
  const field = event.target;
  if (field instanceof HTMLInputElement && field.matches('[data-se2-search-open]') && field.value) {
    se2OpenSearchFromDrawer(field);
  }
});

// With JavaScript on, the drawer field is empty except while the drawer slides out after the first key (typing moves
// to the modal), so its magnifier button or Enter would submit an empty GET /search?q=. Open the modal instead; a
// field with a query still submits normally.
document.addEventListener('submit', (event) => {
  const form = event.target;
  const field = form instanceof HTMLFormElement ? form.querySelector('[data-se2-search-open]') : null;
  if (field instanceof HTMLInputElement && !field.value.trim()) {
    event.preventDefault();
    se2OpenSearchFromDrawer(field);
  }
});

// On touch, take the tap at touchend: cancelling it stops the native focus (no keyboard flash in the closing
// drawer) and the synthetic click, so the click listener above serves mouse users only. A touch that ended a
// scroll is not cancelable and is left alone.
document.addEventListener(
  'touchend',
  (event) => {
    const field = event.target instanceof Element ? event.target.closest('[data-se2-search-open]') : null;
    if (field instanceof HTMLInputElement && event.cancelable) {
      event.preventDefault();
      se2OpenSearchFromDrawer(field);
    }
  },
  { passive: false }
);
